import { type IconDefinition } from '@/components/ui/app-icon';
import type { EngageMailbox } from '@/store/features/engage.feature';
import type { CrmOption } from '@/types';
import {
  CONNECTION_TYPES,
  isConnectionType,
  type AgentConnection,
  type AgentConnectionInput,
  type AgentDto,
  type ConnectionType,
  type PatchAgentBody,
  type ToolKey,
  type ToolPolicy,
} from '@/types/gtm-agents';
import {
  iconBolt,
  iconBrain,
  iconBuildings,
  iconCircleQuestion,
  iconClock,
  iconGlobe,
  iconListUl,
  iconPencil,
  iconPlay,
  iconShieldCheck,
  iconSparkles,
  iconUserMagnifyingGlass,
} from '@/lib/icons';
import { connectionInput, storedConnectionInputs } from './agent-edit.util';
import {
  abilityOn,
  CONNECTION_TOOLS,
  type ConnectionTool,
} from './agent-switches.util';

export {
  abilityChange,
  abilityOn,
  CONNECTION_TOOLS,
  type ConnectionTool,
} from './agent-switches.util';

/**
 * What the Agent tab reads off the agent and the organization's connections,
 * and the PATCH each of its controls writes. Pure, so it is tested here.
 */

// ---------------------------------------------------------------------------
// Connections
// ---------------------------------------------------------------------------

export { CONNECTION_TYPES, isConnectionType };

/**
 * The apps the page offers to add: every one but Attio, which only when the
 * organization's CRM options list it (connected, or open to connect). An
 * agent that already holds Attio still lists it among its connections.
 */
export function offeredConnectionTypes(
  crm: readonly Pick<CrmOption, 'type'>[] | undefined
): ConnectionType[] {
  const attioListed = Boolean(
    crm?.some((item) => item.type.toLowerCase() === 'attio')
  );
  return CONNECTION_TYPES.filter((type) => type !== 'attio' || attioListed);
}

const POLICIES: readonly ToolPolicy[] = ['allow', 'ask', 'never'];

/** The policies a tool offers: nobody can answer a read in an unattended run. */
export function policiesFor(
  tool: Pick<ConnectionTool, 'group'>
): readonly ToolPolicy[] {
  return tool.group === 'read' ? ['allow', 'never'] : POLICIES;
}

/** The policies every one of the tools offers: a read never asks. */
export function policiesForAll(
  tools: readonly Pick<ConnectionTool, 'group'>[]
): readonly ToolPolicy[] {
  return POLICIES.filter((policy) =>
    tools.every((tool) => policiesFor(tool).includes(policy))
  );
}

/**
 * A tool's policy as runs apply it. Unset is Always allow, which is what runs
 * did before anything was enforced. A read stored as Ask each time reads as
 * Never: a read has no one to ask.
 */
export function toolPolicy(
  connection: Pick<AgentConnection, 'policy'> | undefined,
  tool: ConnectionTool
): ToolPolicy {
  const stored = connection?.policy?.[tool.key];
  if (tool.group === 'read')
    return stored === 'ask' ? 'never' : (stored ?? 'allow');
  return stored ?? 'allow';
}

/** The policy every given tool has, or 'mixed'. */
export function commonPolicy(
  connection: Pick<AgentConnection, 'policy'> | undefined,
  tools: readonly ConnectionTool[]
): ToolPolicy | 'mixed' {
  const values = new Set(tools.map((tool) => toolPolicy(connection, tool)));
  return values.size === 1 ? [...values][0] : 'mixed';
}

/**
 * How many of a connection's tools may run: not Never, and the agent's own
 * switch for the tool (`tools`, which Clu can turn off) not off.
 */
export function toolsEnabled(
  connection: Pick<AgentConnection, 'type' | 'policy'>,
  switches: Partial<Record<ToolKey, boolean>> = {}
): { on: number; total: number } {
  const tools = CONNECTION_TOOLS[connection.type];
  return {
    on: tools.filter(
      (tool) =>
        switches[tool.key] !== false && toolPolicy(connection, tool) !== 'never'
    ).length,
    total: tools.length,
  };
}

/**
 * The connection the agent uses for an app: on the agent, stored or only its
 * plays use it (`fromPlay`, read as on with default policies). Undefined once
 * it is removed from the agent, and for an app nothing uses.
 */
export function connectionOf(
  agent: Pick<AgentDto, 'connections'>,
  type: ConnectionType
): AgentConnection | undefined {
  const found = agent.connections.find((item) => item.type === type);
  return found?.enabled ? found : undefined;
}

/** The connections on the agent, in a fixed order. */
export function agentConnections(
  agent: Pick<AgentDto, 'connections'>
): AgentConnection[] {
  return CONNECTION_TYPES.flatMap((type) => {
    const connection = connectionOf(agent, type);
    return connection ? [connection] : [];
  });
}

/** Removed from the agent while its plays use the app: shown as Off. */
export function connectionsOff(
  agent: Pick<AgentDto, 'connections' | 'playConnectionTypes'>
): ConnectionType[] {
  return CONNECTION_TYPES.filter(
    (type) =>
      agent.connections.some((item) => item.type === type && !item.enabled) &&
      Boolean(agent.playConnectionTypes?.includes(type))
  );
}

/** What the agent stores for an app: its connection as it stands, or the defaults. */
function storedOf(
  current: Pick<AgentDto, 'connections'>,
  type: ConnectionType
): AgentConnectionInput {
  const found = current.connections.find((item) => item.type === type);
  return found
    ? connectionInput(found)
    : { type, enabled: true, actingUserId: null, policy: {} };
}

/**
 * The PATCH that stores `next` among the agent's stored connections: in its
 * place, or last when the app wasn't stored (one only its plays use). The
 * other apps its plays use stay unstored.
 */
function writeConnection(
  current: Pick<AgentDto, 'connections'>,
  change: AgentConnectionInput
): PatchAgentBody {
  const stored = storedConnectionInputs(current.connections);
  // Like the others, a read asking goes out as Never.
  const next = connectionInput(change);
  const at = stored.findIndex((item) => item.type === next.type);
  return {
    connections:
      at >= 0
        ? stored.map((item, index) => (index === at ? next : item))
        : [...stored, next],
  };
}

/**
 * Adds a connection to the agent, or turns a stored one back on. One the agent
 * has had keeps who it acts as (the owner while no one is picked), which is who
 * approves what it holds: the member who turns it back on does not become the
 * approver. The member adding it is only for a connection it never had, and
 * only on an app that shows who it acts as: a new Attio connection acts as the
 * agent's owner.
 */
export function addConnection(
  current: Pick<AgentDto, 'connections'>,
  type: ConnectionType,
  actingUserId: string | null
): PatchAgentBody {
  const had = current.connections.some((item) => item.type === type);
  const existing = storedOf(current, type);
  return writeConnection(current, {
    ...existing,
    enabled: true,
    actingUserId: had
      ? existing.actingUserId
      : hasActsAs(type)
        ? actingUserId
        : null,
  });
}

/**
 * Takes a connection off the agent. It stays stored, off: an app its plays use
 * that was never stored reads as on.
 */
export function removeConnection(
  current: Pick<AgentDto, 'connections'>,
  type: ConnectionType
): PatchAgentBody {
  return writeConnection(current, {
    ...storedOf(current, type),
    enabled: false,
  });
}

/**
 * One connection changed: who it acts as, or its tools' policies. An app only
 * its plays use is stored with the change.
 */
export function changeConnection(
  current: Pick<AgentDto, 'connections'>,
  type: ConnectionType,
  change: {
    actingUserId?: string | null;
    policy?: Partial<Record<ToolKey, ToolPolicy>>;
  }
): PatchAgentBody {
  const existing = storedOf(current, type);
  return writeConnection(current, {
    ...existing,
    ...(change.actingUserId !== undefined
      ? { actingUserId: change.actingUserId }
      : {}),
    ...(change.policy
      ? { policy: { ...existing.policy, ...change.policy } }
      : {}),
  });
}

/**
 * The policy for every given tool set to one value, skipping a tool that
 * doesn't offer it (a read never asks).
 */
export function policyFor(
  tools: readonly ConnectionTool[],
  policy: ToolPolicy
): Partial<Record<ToolKey, ToolPolicy>> {
  return Object.fromEntries(
    tools
      .filter((tool) => policiesFor(tool).includes(policy))
      .map((tool) => [tool.key, policy])
  );
}

// ---------------------------------------------------------------------------
// Acting user
// ---------------------------------------------------------------------------

export interface MemberLike {
  user_id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export function memberName(member: MemberLike): string {
  return (
    [member.first_name, member.last_name].filter(Boolean).join(' ') ||
    member.email ||
    'Member'
  );
}

/**
 * Who a connection acts as, as the API says: the member picked, or the agent's
 * owner while no one is; `left` once that person left the workspace (writes on
 * the connection are then blocked); unknown when the API didn't say.
 */
export type ActsAs =
  | { kind: 'owner' | 'member' | 'left'; name: string }
  | { kind: 'unknown' };

export function actsAs(
  connection: Pick<AgentConnection, 'actsAs' | 'actsAsOwner'> | undefined
): ActsAs {
  const person = connection?.actsAs;
  if (!person) return { kind: 'unknown' };
  if (person.left) return { kind: 'left', name: person.name };
  return {
    kind: connection?.actsAsOwner ? 'owner' : 'member',
    name: person.name,
  };
}

/**
 * Whether the page shows who an app acts as. Attio's token belongs to the
 * workspace and its writes show as the Cleanlist app, so Attio doesn't.
 */
export function hasActsAs(type: ConnectionType): boolean {
  switch (type) {
    case 'salesforce':
    case 'hubspot':
    case 'slack':
    case 'email':
      return true;
    case 'attio':
      return false;
  }
}

/**
 * Whether to show who a connection acts as: always on an app that has it,
 * and on Attio only once that person left, since their leaving blocks its
 * writes and the page must say so.
 */
export function actsAsShown(type: ConnectionType, acting: ActsAs): boolean {
  return hasActsAs(type) || acting.kind === 'left';
}

/** The "Acts as" value: "Agent owner (Jane)", "Jane" or "Jane (left)". */
export function actsAsValue(acting: ActsAs): string | null {
  switch (acting.kind) {
    case 'owner':
      return 'Agent owner (' + acting.name + ')';
    case 'member':
      return acting.name;
    case 'left':
      return acting.name + ' (left)';
    default:
      return null;
  }
}

/** The connections list's line: "Acts as Jane", "Agent owner (Jane)". */
export function actsAsLine(acting: ActsAs): string | null {
  return acting.kind === 'member'
    ? 'Acts as ' + acting.name
    : actsAsValue(acting);
}

/** Whom the CRM shows as the writer, "Dana" or "Dana (left)"; CRM only. */
export function connectedByValue(
  connection: Pick<AgentConnection, 'connectedBy'> | undefined
): string | null {
  const person = connection?.connectedBy;
  return person ? person.name + (person.left ? ' (left)' : '') : null;
}

/** Where a connection is set up for the organization. */
export function connectionSetupUrl(type: ConnectionType): string {
  return type === 'email'
    ? '/sequencing/settings?section=senders'
    : '/integrations';
}

export interface ConnectionHealth {
  /** The organization has it set up and it works right now. */
  live: boolean;
  /** Set up once and broken since. */
  needsReconnect: boolean;
  /** The account, when known: the Slack workspace, the mailboxes. */
  account: string | null;
}

/** Whether the organization's side of a connection is live. */
export function connectionHealth(
  type: ConnectionType,
  sources: {
    crm: readonly CrmOption[] | undefined;
    slack:
      | {
          connected?: boolean;
          reconnectRequired?: boolean;
          teamName?: string | null;
        }
      | undefined;
    mailboxes: readonly Pick<EngageMailbox, 'connected' | 'email_address'>[];
  }
): ConnectionHealth {
  // A CRM's health is its option: a revoked Attio token reads disconnected.
  if (type === 'salesforce' || type === 'hubspot' || type === 'attio') {
    const option = sources.crm?.find(
      (item) => item.type.toLowerCase() === type
    );
    return {
      live: Boolean(option?.onboarded && option.is_connected),
      needsReconnect: Boolean(option?.onboarded && !option.is_connected),
      account: null,
    };
  }
  if (type === 'slack')
    return {
      live: Boolean(sources.slack?.connected),
      needsReconnect: Boolean(sources.slack?.reconnectRequired),
      account: sources.slack?.teamName ?? null,
    };
  const connected = sources.mailboxes.filter((mailbox) => mailbox.connected);
  return {
    live: connected.length > 0,
    needsReconnect: sources.mailboxes.length > 0 && connected.length === 0,
    account:
      connected.map((mailbox) => mailbox.email_address).join(', ') || null,
  };
}

// ---------------------------------------------------------------------------
// Abilities: the agent's tools that are not a connection's
// ---------------------------------------------------------------------------

export interface Ability {
  key: ToolKey;
  label: string;
  icon: IconDefinition;
}

export const ABILITIES: readonly Ability[] = [
  { key: 'find_people', label: 'Find people', icon: iconUserMagnifyingGlass },
  { key: 'find_companies', label: 'Find companies', icon: iconBuildings },
  { key: 'enrich', label: 'Enrich', icon: iconSparkles },
  { key: 'validate', label: 'Validate', icon: iconShieldCheck },
  { key: 'record_skills', label: 'Record skills', icon: iconBolt },
  { key: 'lists', label: 'Lists', icon: iconListUl },
  { key: 'web_search', label: 'Web search', icon: iconGlobe },
  { key: 'ask_user', label: 'Ask me questions', icon: iconCircleQuestion },
  { key: 'run_play', label: 'Run the play', icon: iconPlay },
  { key: 'manage_triggers', label: 'Manage triggers', icon: iconClock },
  {
    key: 'edit_instructions',
    label: 'Edit its instructions',
    icon: iconPencil,
  },
  { key: 'memory', label: 'Memory', icon: iconBrain },
];

/** Every tool switch's name as the API says it ("Enrich", "Write records"). */
const TOOL_LABELS: ReadonlyMap<string, string> = new Map([
  ...ABILITIES.map((ability) => [ability.key, ability.label] as const),
  ...CONNECTION_TYPES.flatMap((type) => CONNECTION_TOOLS[type]).map(
    (tool) => [tool.key, tool.label] as const
  ),
]);

/** The Abilities, and the four tool switches the page shows on a connection. */
export function isToolKey(value: unknown): value is ToolKey {
  return typeof value === 'string' && TOOL_LABELS.has(value);
}

export function toolLabel(key: ToolKey): string {
  return TOOL_LABELS.get(key) ?? key;
}

export function abilitiesOn(
  agent: Pick<AgentDto, 'tools'> &
    Partial<Pick<AgentDto, 'aiManagedTriggers' | 'selfEditInstructions'>>
): number {
  return ABILITIES.filter((ability) => abilityOn(agent, ability.key)).length;
}
